import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';
import { VeterinarianOffice } from '@/models/DTO/veterinarian-office/veterinarian-office.model';
import { Inject } from 'inversify-props';
import { VeterinarianOfficesService } from '@/services/veterinarian-offices.service';
import { VeterinarianMutation } from '@/constants/mutation.constants';
import { VeterinarianActions } from '@/constants/actions.constants';
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model';
import { VeterinarianOfficeUser } from '@/models/DTO/veterinarian-office-user/veterinarian-office-user.model';
import { VeterinarianGetters } from '@/constants/getters.constants';

export const VETERINARIAN_MODULE_NAME = 'veterinarian';

class DefaultState {
  public veterinarianOffices: VeterinarianOffice[] = [];
  public showLoading = false;
  public showButtonLoading = false;
  public showTableLoading = false;
  public errorResponse: BaseAPIErrorModel | null = null;
  public currentVeterinarianOffice: VeterinarianOffice | null = null;
  public currentVeterinarianOfficeUsers: VeterinarianOfficeUser[] | null = null;
  public selectedVeterinarianUserId = '';
  public selectedVeterinarianOfficeId = '';
  public newVeterinarianOffice: VeterinarianOffice | null = null;
}

const defaultState = new DefaultState();

@Module({
  namespaced: true,
  name: VETERINARIAN_MODULE_NAME
})
export default class VeterinarianModule extends VuexModule {
  // #region Services
  @Inject()
  private readonly _veterinarianOfficesService!: VeterinarianOfficesService;
  // #endregion

  // #region State
  public veterinarianOffices: VeterinarianOffice[] =
    defaultState.veterinarianOffices;

  // Use with full page loading
  public showLoading = defaultState.showLoading;

  // Show on button only
  public showButtonLoading = defaultState.showButtonLoading;

  // Use with all gets for a table
  public showTableLoading = defaultState.showTableLoading;

  public errorResponse: BaseAPIErrorModel | null = defaultState.errorResponse;
  public currentVeterinarianOffice: VeterinarianOffice | null =
    defaultState.currentVeterinarianOffice;
  public currentVeterinarianOfficeUsers: VeterinarianOfficeUser[] | null =
    defaultState.currentVeterinarianOfficeUsers;
  public selectedVeterinarianUserId = defaultState.selectedVeterinarianUserId;
  public selectedVeterinarianOfficeId =
    defaultState.selectedVeterinarianOfficeId;
  public newVeterinarianOffice: VeterinarianOffice | null =
    defaultState.newVeterinarianOffice;
  // #endregion

  // #region Getters
  public get [VeterinarianGetters.hasErrorMessages](): boolean {
    return this.context.getters[VeterinarianGetters.errorMessages].length > 0;
  }

  public get [VeterinarianGetters.errorMessages](): string[] {
    return this.errorResponse?.getAllErrorMessages?.() ?? [];
  }
  // #endregion

  // #region Actions
  @Action
  public async [VeterinarianActions.getVeterinarianOfficesAction](): Promise<
    void
  > {
    try {
      this.context.commit(VeterinarianMutation.showTableLoadingMutation, true);

      // remove any errors
      this.context.commit(VeterinarianMutation.errorMutation, null);

      const resp = await this._veterinarianOfficesService.getVeterinarianOffices();

      // success
      this.context.commit(VeterinarianMutation.setVeterinarianOfficesMutation, [
        ...resp
      ]);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianMutation.errorMutation, e);
    } finally {
      this.context.commit(VeterinarianMutation.showTableLoadingMutation, false);
    }
  }

  @Action
  public async [VeterinarianActions.getVeterinarianOfficeByIdAction](
    id: string
  ): Promise<void> {
    try {
      this.context.commit(VeterinarianMutation.showLoadingMutation, true);

      // remove any errors
      this.context.commit(VeterinarianMutation.errorMutation, null);

      const resp = await this._veterinarianOfficesService.getVeterinarianOfficeById(
        id
      );

      // success
      this.context.commit(
        VeterinarianMutation.setCurrentVeterinarianOfficeMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianMutation.errorMutation, e);
    } finally {
      this.context.commit(VeterinarianMutation.showLoadingMutation, false);
    }
  }

  @Action
  public async [VeterinarianActions.createVeterinarianOfficeAction](
    model: VeterinarianOffice
  ): Promise<void> {
    try {
      this.context.commit(VeterinarianMutation.showButtonLoadingMutation, true);

      // remove any errors
      this.context.commit(VeterinarianMutation.errorMutation, null);

      const resp = await this._veterinarianOfficesService.createVeterinarianOffice(
        {
          ...model
        }
      );

      // Get updated list of offices, perform asynchronously
      this.context.dispatch(VeterinarianActions.getVeterinarianOfficesAction);

      // success
      this.context.commit(
        VeterinarianMutation.setCurrentVeterinarianOfficeMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianMutation.errorMutation, e);
    } finally {
      this.context.commit(
        VeterinarianMutation.showButtonLoadingMutation,
        false
      );
    }
  }

  @Action
  public async [VeterinarianActions.editVeterinarianOfficeAction](
    model: VeterinarianOffice
  ): Promise<void> {
    try {
      this.context.commit(VeterinarianMutation.showButtonLoadingMutation, true);

      // remove any errors
      this.context.commit(VeterinarianMutation.errorMutation, null);

      const resp = await this._veterinarianOfficesService.editVeterinarianOffice(
        {
          ...model
        }
      );

      // Get updated list of offices, perform asynchronously
      this.context.dispatch(VeterinarianActions.getVeterinarianOfficesAction);

      // success
      this.context.commit(
        VeterinarianMutation.setCurrentVeterinarianOfficeMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianMutation.errorMutation, e);
    } finally {
      this.context.commit(
        VeterinarianMutation.showButtonLoadingMutation,
        false
      );
    }
  }

  @Action
  public async [VeterinarianActions.deleteVeterinarianOfficeAction](
    id: string
  ): Promise<void> {
    try {
      this.context.commit(VeterinarianMutation.showLoadingMutation, true);

      // remove any errors
      this.context.commit(VeterinarianMutation.errorMutation, null);

      const resp = await this._veterinarianOfficesService.deleteVeterinarianOffice(
        id
      );

      // success
      this.context.commit(
        VeterinarianMutation.setCurrentVeterinarianOfficeMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianMutation.errorMutation, e);
    } finally {
      this.context.commit(VeterinarianMutation.showLoadingMutation, false);
    }
  }

  @Action
  public async [VeterinarianActions.setSelectedVeterinarianUserIdAction](
    userId: string
  ): Promise<void> {
    return this.context.commit(
      VeterinarianMutation.setSelectedVeterinarianUserIdMutation,
      userId
    );
  }

  @Action
  public async [VeterinarianActions.setSelectedVeterinarianOfficeIdAction](
    officeId: string
  ): Promise<void> {
    return this.context.commit(
      VeterinarianMutation.setSelectedVeterinarianOfficeIdMutation,
      officeId
    );
  }

  @Action
  public async [VeterinarianActions.getAllUsersForOfficeAction](
    id: string
  ): Promise<void> {
    try {
      this.context.commit(VeterinarianMutation.showTableLoadingMutation, true);

      // remove any errors
      this.context.commit(VeterinarianMutation.errorMutation, null);

      const resp = await this._veterinarianOfficesService.getAllUsersForOffice(
        id
      );

      // success
      this.context.commit(
        VeterinarianMutation.setCurrentVeterinarianOfficeUsersMutation,
        [...resp]
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianMutation.errorMutation, e);
    } finally {
      this.context.commit(VeterinarianMutation.showTableLoadingMutation, false);
    }
  }

  @Action
  public async [VeterinarianActions.resetStateAction](): Promise<void> {
    this.context.commit(VeterinarianMutation.resetStateMutation);
  }

  @Action
  public async [VeterinarianActions.resetVeterinarianOfficeUsers](): Promise<
    void
  > {
    this.context.commit(VeterinarianMutation.resetVeterinarianOfficeUsers);
  }

  @Action
  public async [VeterinarianActions.newVeterinarianOfficeAction](
    payload: VeterinarianOffice | null
  ): Promise<void> {
    this.context.commit(
      VeterinarianMutation.newVeterinarianOfficeMutation,
      payload
    );
  }
  // #endregion

  // #region Mutation
  @Mutation
  private [VeterinarianMutation.showLoadingMutation](show: boolean) {
    this.showLoading = show;
  }

  @Mutation
  private [VeterinarianMutation.showButtonLoadingMutation](show: boolean) {
    this.showButtonLoading = show;
  }

  @Mutation
  private [VeterinarianMutation.showTableLoadingMutation](show: boolean) {
    this.showTableLoading = show;
  }

  @Mutation
  private [VeterinarianMutation.errorMutation](
    errorModel: BaseAPIErrorModel | null
  ) {
    this.errorResponse = errorModel;
  }

  @Mutation
  private [VeterinarianMutation.setVeterinarianOfficesMutation](
    data: VeterinarianOffice[]
  ) {
    this.veterinarianOffices = data ? [...data] : [];
  }

  @Mutation
  private [VeterinarianMutation.setCurrentVeterinarianOfficeMutation](
    data: VeterinarianOffice
  ) {
    this.currentVeterinarianOffice = { ...data };
  }

  @Mutation
  private [VeterinarianMutation.setCurrentVeterinarianOfficeUsersMutation](
    data: VeterinarianOfficeUser[]
  ) {
    this.currentVeterinarianOfficeUsers = [...data];
  }

  @Mutation
  private [VeterinarianMutation.setSelectedVeterinarianUserIdMutation](
    userId: string
  ) {
    this.selectedVeterinarianUserId = userId;
  }

  @Mutation
  private [VeterinarianMutation.setSelectedVeterinarianOfficeIdMutation](
    officeId: string
  ) {
    this.selectedVeterinarianOfficeId = officeId;
  }

  @Mutation
  private [VeterinarianMutation.resetStateMutation]() {
    Object.assign(this, defaultState);
  }

  @Mutation
  private [VeterinarianMutation.resetVeterinarianOfficeUsers]() {
    this.currentVeterinarianOfficeUsers =
      defaultState.currentVeterinarianOfficeUsers;
  }

  @Mutation
  private [VeterinarianMutation.newVeterinarianOfficeMutation](
    office: VeterinarianOffice | null
  ) {
    if (!office) {
      this.newVeterinarianOffice = null;
    } else {
      this.newVeterinarianOffice = { ...office };
    }
  }
  // #endregion
}
